import { useMemo } from "react"

// ** Third Party Components
import { format } from "date-fns"
import { he as heLocale } from "date-fns/locale"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Types
import { DictType } from "@pma/types"

// ** Utils
import { formatCurrency } from "@pma/utils/utils"
import { cn, getUserFromId } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import BillPrintTable from "./bill-print-table"

interface Props {
  bill: DictType
  status: any
}

const Invoice = ({ bill, status }: Props) => {
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()

  const isHebrew = locale === "he"

  const isSigned = bill?.is_signed
  const isApproved = bill?.is_approved
  const isDeclined = bill?.is_declined
  const orgId = authConfig?.last_organization_id
  const isSignedAndApproved = isSigned && isApproved
  const organizationUsers = authConfig?.organizationUsers
  const availableWorkspaces = authConfig?.companies_of_user
  const currentOrg = availableWorkspaces?.find((org: DictType) => org?.id === orgId)

  const subtotal = useMemo(() => {
    if (!bill?._bill_items) return 0
    if (isApproved || isSigned) {
      return bill._bill_items
        .filter((item: DictType) => item?._boq_item?.is_item)
        .reduce((s: number, item: DictType) =>
          s + ((Number(item?.approved_amount) || 0) * (Number(item?._boq_item?.boq_cost) || 0)), 0)
    }
    return bill._bill_items
      .filter((item: DictType) => item?._boq_item?.is_item)
      .reduce((s: number, item: DictType) =>
        s + ((Number(item?.amount) || 0) * (Number(item?._boq_item?.boq_cost) || 0)), 0)
  }, [bill?._bill_items, isApproved, isSigned])

  // totalNet = sum of requested_sum / approved_sum (pre-computed, after discount)
  const totalNet = useMemo(() => {
    if (!bill?._bill_items) return 0
    if (isApproved || isSigned) {
      return bill._bill_items
        .filter((item: DictType) => item?._boq_item?.is_item)
        .reduce((s: number, item: DictType) => s + (Number(item?.approved_sum) || 0), 0)
    }
    return bill._bill_items
      .filter((item: DictType) => item?._boq_item?.is_item)
      .reduce((s: number, item: DictType) => s + (Number(item?.requested_sum) || 0), 0)
  }, [bill?._bill_items, isApproved, isSigned])

  const discount = subtotal - totalNet
  const vatPercent = bill?.vat_percent ?? 18
  const totalAfterRetention = totalNet - (bill?.retention_sum_req || 0)
  const totalTax = totalAfterRetention * (vatPercent / 100)
  const totalBill = totalAfterRetention + totalTax

  const contractor: DictType = useMemo(() => {
    if (!!bill?.user_id) {
      return getUserFromId(bill?.user_id, organizationUsers)
    }
    return {}
  }, [bill])

  const formattedDate = useMemo(() => {
    try {
      const date = new Date(bill?.created_at)
      return isHebrew ? format(date, "dd/MM/yyyy", { locale: heLocale }) : format(date, "P")
    } catch {
      return format(new Date(bill?.created_at), "P")
    }
  }, [bill?.created_at, isHebrew])

  return (
    <div className='print:p-6'>
      <div
        id={`bill-${bill?.number}`}
        className='relative w-full overflow-hidden rounded-lg border-2 border-gray-200 bg-white p-4 shadow-lg sm:p-6 md:p-8 print:border print:p-6 print:shadow-none'
        dir={isHebrew ? "rtl" : "ltr"}
      >
        {/* Watermark */}
        <div
          className='pointer-events-none absolute inset-0 flex items-center justify-center print:flex'
          aria-hidden='true'
        >
          <span
            className='select-none whitespace-nowrap text-7xl font-bold tracking-widest text-gray-100 sm:text-8xl'
            style={{ transform: "rotate(-30deg)" }}
          >
            {isHebrew ? "\u05DE\u05E7\u05D5\u05E8\u05D9" : "ORIGINAL"}
          </span>
        </div>

        {/* Header Section */}
        <div className='relative mb-6 flex flex-col items-start justify-between gap-4 border-b-2 border-gray-300 pb-5 sm:flex-row'>
          <div className='flex w-full items-center gap-3 sm:w-auto'>
            {currentOrg?.company_logo && (
              <img
                src={currentOrg?.company_logo?.url || "/placeholder.svg"}
                alt={currentOrg?.organization_name}
                className='sm:h-18 sm:w-18 h-14 w-14 flex-shrink-0 rounded-lg border border-gray-100 bg-white object-contain p-2 shadow-sm md:h-20 md:w-20'
              />
            )}
            <div className='min-w-0'>
              <h1 className='mb-1 text-2xl font-bold tracking-tight text-blue-600 sm:text-3xl'>
                {translate("INVOICE")}
              </h1>
              <div className='font-mono text-sm font-semibold tabular-nums text-gray-600 sm:text-base md:text-lg'>
                #{bill?.number}
              </div>
            </div>
          </div>
          <div className={cn("w-full sm:w-auto", isHebrew ? "sm:text-left" : "sm:text-right")}>
            <h2 className='break-words text-base font-semibold tracking-tight text-gray-800 sm:text-lg md:text-xl'>
              {currentOrg?.organization_name}
            </h2>
          </div>
        </div>

        {/* Separator */}
        <div className='mb-6 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent' />

        {/* Info Section */}
        <div className='relative mb-6 flex flex-col justify-between gap-6 lg:flex-row'>
          {/* Contractor Info */}
          <div className='flex-1'>
            <h3 className='mb-2 text-sm font-medium text-foreground'>{translate("Contractor")}:</h3>
            <div className='space-y-1 text-sm text-gray-700'>
              <p className='break-words font-medium'>{contractor?.name}</p>
              <p className='break-all text-muted-foreground'>{contractor?.phone}</p>
              <p className='break-all text-muted-foreground'>{contractor?.email}</p>
            </div>
          </div>

          {/* Date and Status */}
          <div className={cn("flex-1", isHebrew ? "lg:text-left" : "lg:text-right")}>
            <div className='space-y-2 text-sm'>
              <div className='flex justify-between gap-4 lg:justify-end'>
                <span className='font-medium text-gray-700'>{translate("Date")}:</span>
                <span className='text-gray-600'>{formattedDate}</span>
              </div>
              <div className='flex justify-between gap-4 lg:justify-end'>
                <span className='font-medium text-gray-700'>{translate("Status")}:</span>
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-xs font-semibold",
                    isDeclined && "bg-red-100 text-red-700",
                    isSignedAndApproved && "bg-green-100 text-green-700",
                    isApproved && !isSigned && "bg-blue-100 text-blue-700",
                    !isSignedAndApproved &&
                      !isDeclined &&
                      !isApproved &&
                      "bg-gray-100 text-gray-700",
                  )}
                >
                  {isApproved && !isSigned && translate("Approved")}
                  {isDeclined && translate("Declined")}
                  {isSignedAndApproved && translate("Paid")}
                  {!isSignedAndApproved &&
                    !isDeclined &&
                    !isApproved &&
                    translate("Pending Approval")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className='mb-4 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent' />

        <BillPrintTable
          data={
            bill?._bill_items
              ?.map((item: any) => ({ ...item._boq_item, bill: item }))
              .filter((item: DictType) => item?.amount) || []
          }
          isApproved={isApproved}
          status={status}
        />

        {/* Totals Section */}
        <div className='relative mb-6 flex justify-end'>
          <div className='w-full space-y-2 rounded-lg bg-gray-50 p-4 text-sm sm:w-80 sm:text-base'>
            <div className='flex justify-between border-b border-gray-200 py-2'>
              <strong className='text-sm font-medium text-gray-700'>
                {translate("subtotal")}:
              </strong>
              <span className='font-mono font-medium tabular-nums text-gray-700'>
                {formatCurrency(subtotal, isHebrew)}
              </span>
            </div>
            {discount > 0 && (
              <div className='flex justify-between border-b border-gray-200 py-2'>
                <strong className='text-sm font-medium text-gray-700'>
                  {translate("Discount")} (-):
                </strong>
                <span className='font-mono font-medium tabular-nums text-red-600'>
                  -{formatCurrency(discount, isHebrew)}
                </span>
              </div>
            )}
            <div className='flex justify-between border-b border-gray-200 py-2'>
              <strong className='text-sm font-medium text-gray-700'>
                {translate("total after discount")}:
              </strong>
              <span className='font-mono font-medium tabular-nums text-gray-700'>
                {formatCurrency(totalNet, isHebrew)}
              </span>
            </div>
            <div className='flex justify-between border-b border-gray-200 py-2'>
              <strong className='text-sm font-medium text-gray-700'>
                {translate("Retention")} (-):
              </strong>
              <span className='font-mono font-medium tabular-nums text-gray-700'>
                {formatCurrency(bill?.retention_sum_req, isHebrew)}
              </span>
            </div>
            <div className='flex justify-between border-b border-gray-200 py-2'>
              <strong className='text-sm font-medium text-gray-700'>
                {translate("Total after retention")}:
              </strong>
              <span className='font-mono font-medium tabular-nums text-gray-700'>
                {formatCurrency(totalAfterRetention, isHebrew)}
              </span>
            </div>
            <div className='flex justify-between border-gray-200 py-2'>
              <strong className='text-sm font-medium text-gray-700'>
                {translate("VAT")} ({vatPercent}%):
              </strong>
              <span className='font-mono font-medium tabular-nums text-gray-700'>
                {formatCurrency(totalTax, isHebrew)}
              </span>
            </div>
            <div className='flex justify-between border-t-2 border-gray-400 py-3'>
              <strong className='text-base font-bold text-gray-900 sm:text-lg'>
                {translate("Total")}:
              </strong>
              <span className='font-mono text-base font-bold tabular-nums text-gray-900 sm:text-lg'>
                {formatCurrency(totalBill, isHebrew)}
              </span>
            </div>
          </div>
        </div>

        {/* Note Section */}
        {!!bill?.message?.user_id && (
          <div className='relative mb-4 border-t border-gray-200 pt-4'>
            <h3 className='mb-2 text-sm font-medium text-foreground'>{translate("Note")}:</h3>
            <p className='break-words text-sm text-gray-700'>{bill?.message?.text}</p>
          </div>
        )}

        {/* Signatures Section */}
        {(() => {
          const signs: DictType[] = bill?._signs || bill?._sign || []
          if (!signs.length) return null
          return (
            <div className='relative mb-4 border-t border-gray-200 pt-4'>
              <h3 className='mb-3 text-sm font-medium text-foreground'>{translate("signatures")}:</h3>
              <div className='flex flex-wrap gap-6'>
                {signs.map((sign: DictType) => (
                  <div key={sign?.id} className='flex flex-col items-center gap-1 text-center'>
                    {sign?.signature?.url && (
                      <img
                        src={sign.signature.url}
                        alt={sign?._user?.name || ""}
                        loading='eager'
                        className='h-16 w-32 object-contain'
                      />
                    )}
                    <div className='h-px w-32 bg-gray-400' />
                    <p className='text-xs font-medium text-gray-800'>{sign?._user?.name}</p>
                    {sign?.created_at && (
                      <p className='text-xs text-gray-500'>
                        {format(new Date(sign.created_at), "dd/MM/yyyy")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })()}

        {/* Footer */}
        <div className='relative mt-8 border-t border-gray-200 pt-4 text-center'>
          <p className='text-xs text-muted-foreground'>{translate("invoice_generated_by")}</p>
          <p className='mt-1 text-xs text-gray-400'>
            {translate("All Rights Reserved")} - {translate("Rog Tech Systems")} &copy;{" "}
            {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  )
}

export default Invoice
